import { createDocument, createPictogram } from "../api/pictogram.api"
import { uploadFile } from "@pictograms/shared/services/uppy-client.service"
import { CadWorkerClient } from "../../cad/worker/CadWorkerClient"
import { createLogger } from "../../utils/logger"
import { addDocumentVersionForPictogram } from "../api/pictogram.api"
import { PICTOGRAM_TYPES } from "../../constants/pictogram-types"
const logger = createLogger('pictogram.service')

/**
 * Crea un pictograma desde cero: sube DXF, sube scene, sube screenshots, persiste.
 * @param {Object} opts
 * @param {Number|String}   opts.entityId
 * @param {File}            opts.dxfFile          — el DXF original que cargó el usuario
 * @param {Object}          opts.cadJson          — processedCadJson tras worker
 * @param {Array}           opts.screenshots      — [{name, src: Blob}]
 * @param {String}          opts.name
 * @param {String|null}     opts.code
 * @param {Array}           opts.layers           — descriptores _GetLayers()
 * @param {Object}          opts.cadLayeredDesign — config del modal Configurar
 * @param {Function}        opts.onProgress       — (textKey) => void   notifica fase actual
 * @returns {Promise<Object>} pictogram creado
 */
export async function savePictogram(opts) {
  // Validaciones tempranas con mensajes claros (uppy rechaza con "Archivo inválido" si algo es null).
  if (!opts.dxfFile || !opts.dxfFile.name) {
    logger.error('dxfFile invalid:', opts.dxfFile)
    throw new Error('upload_document_error')
  }
  if (!opts.cadJson) {
    logger.error('cadJson empty')
    throw new Error('upload_scene_error')
  }

  // Default a MECHANICAL si no llega el tipo desde el caller (back-compat).
  const type = opts.type || PICTOGRAM_TYPES.MECHANICAL
  // El scene parseado (CadSceneBuilder.CreateObject) + su subida a S3 solo
  // tienen sentido para mechanical_installation, único tipo que hoy lo consume
  // (viewer-konva, Mongo Components, cálculos de progreso). Para electrical/
  // trenches/evacuation skipeamos: ahorramos tiempo del worker, memoria del
  // cliente y storage en S3.
  const persistScene = type === PICTOGRAM_TYPES.MECHANICAL

  // 1. DXF original (siempre, todos los tipos)
  opts.onProgress?.('uploading_cad_file')
  const dxfAws = await uploadFile(opts.dxfFile)
  if (!dxfAws) throw new Error('upload_document_error')

  const docForm = new FormData()
  docForm.append('original_filename', opts.dxfFile.name)
  docForm.append('entity_type', 'pictogram')
  docForm.append('entity_id',   '')
  docForm.append('project_id',  opts.entityId)
  docForm.append('aws_response', JSON.stringify(dxfAws))

  const docRes = await createDocument(opts.entityId, docForm)
  if (!docRes?.data?.success) throw new Error(docRes?.data?.msg || 'upload_document_error')
  const document = docRes.data.document

  // 2. Scene parseado en S3 — dos flavors:
  //
  //   - mechanical_installation: se genera con el worker (CadSceneBuilder)
  //     que produce {entities, layers, origin, bounds} en JSON. Lo consumen
  //     viewer-konva, diff de versiones y Mongo Components.
  //
  //   - electrical_installation / electrical_trenches: el LayoutBuilder ya
  //     produjo un ArrayBuffer binario (.epcb v1) con encodeScene() y lo
  //     subió. El caller nos pasa el `sceneAws` resultante de ese upload —
  //     acá solo lo propagamos al POST para que el backend persista el path.
  //
  //   - cualquier otro tipo: sin scene.
  let sceneAws = opts.sceneAws ?? null
  if (persistScene && !sceneAws) {
    opts.onProgress?.('uploading_scene')

    const worker = new CadWorkerClient()
    let scene
    try {
      scene = await worker.CreateObject(opts.cadJson)
    } finally {
      await worker.Destroy()
    }

    // El backend espera `entities` como object indexado por `id` (no como array)
    // para hacer lookup O(1) por id. Replica el comportamiento legacy de
    // _FlattenEntities en components/save-pictogram.vue.
    if (Array.isArray(scene?.entities)) {
      const byId = {}
      for (const e of scene.entities) {
        if (e.id != null) byId[e.id] = e
      }
      scene.entities = byId
    }

    const sceneName = document.filename.replace(/\.[^.]+$/, '.json')
    const sceneFile = new File(
      [JSON.stringify(scene)],
      sceneName,
      { type: 'application/json' }
    )
    sceneAws = await uploadFile(sceneFile)
    if (!sceneAws) throw new Error('upload_scene_error')
  }

  // 3. Screenshots (siempre, todos los tipos — útiles incluso sin viewer interactivo)
  opts.onProgress?.('uploading_screenshots')
  const awsImages = []
  for (const s of opts.screenshots) {
    const shotFile = new File([s.src], s.name, { type: s.src.type || 'image/png' })
    const aws = await uploadFile(shotFile)
    if (aws) awsImages.push(aws)
  }

  // 4. POST pictograma
  opts.onProgress?.('saving_pictogram')
  const picForm = new FormData()
  picForm.append('document_id',  document.id)
  picForm.append('code',         opts.code || '')
  picForm.append('name',         opts.name)
  picForm.append('type',         type)
  picForm.append('layers',       JSON.stringify(opts.layers))
  picForm.append('cad_layers',   JSON.stringify(opts.cadLayeredDesign))
  picForm.append('aws_images',   JSON.stringify(awsImages))
  if (sceneAws) {
    picForm.append('aws_response', JSON.stringify(sceneAws))
  }

  const picRes = await createPictogram(opts.entityId, picForm)
  if (!picRes?.data?.success) throw new Error(picRes?.data?.msg || 'save_pictogram_error')
  return picRes.data.pictogram
}

/**
 * Sube una nueva versión del DXF de un pictograma existente:
 *   1. DXF → temp S3 (uppy)
 *   2. Scene JSON parseado → temp S3 (uppy)
 *   3. POST add_version con scene_temp_key — crea DocumentVersion + PendingPictogramVersion
 *      en backend con scene_temp_key listo para retomar el review tras cerrar el browser.
 *
 * NO persiste cambios en el pictograma. Solo prepara los archivos para que el
 * caller redirija a la página de preview/confirm con { documentVersionId, sceneKey }.
 *
 * Si el backend rechaza con `pending_version_exists` (ya hay una versión pendiente
 * para este pictograma) lanza error con name='PendingVersionExists' y prop
 * `pendingDocumentVersionId` — el caller debe mostrar UI específica.
 *
 * @param {Object} opts
 * @param {File}            opts.dxfFile          — el DXF original que cargó el usuario
 * @param {Object}          opts.cadJson          — processedCadJson tras worker
 * @param {Number|String}   opts.documentId       — pictogram.document_id
 * @param {Number|String}   opts.pictogramId      — pictogram.id
 * @param {Function}        opts.onProgress       — (textKey) => void
 * @returns {Promise<{documentVersionId: number, sceneKey: string}>}
 */
export async function uploadNewPictogramVersion(opts) {
  if (!opts.dxfFile || !opts.dxfFile.name) {
    logger.error('dxfFile invalid:', opts.dxfFile)
    throw new Error('upload_document_error')
  }
  if (!opts.cadJson) {
    logger.error('cadJson empty')
    throw new Error('upload_scene_error')
  }
  if (!opts.documentId) {
    throw new Error('missing_document_id')
  }
  if (!opts.pictogramId) {
    throw new Error('missing_pictogram_id')
  }

  // 1. DXF → temp S3
  opts.onProgress?.('uploading_cad_file')
  const dxfAws = await uploadFile(opts.dxfFile)
  if (!dxfAws) throw new Error('upload_document_error')

  // 2. Scene JSON parseado → temp S3
  // Procesado en worker para no congelar UI con archivos grandes.
  opts.onProgress?.('uploading_scene')

  const worker = new CadWorkerClient()
  let scene
  try {
    scene = await worker.CreateObject(opts.cadJson)
  } finally {
    await worker.Destroy()
  }

  // Backend espera entities indexadas por id (lookup O(1)). Replica savePictogram.
  if (Array.isArray(scene?.entities)) {
    const byId = {}
    for (const e of scene.entities) {
      if (e.id != null) byId[e.id] = e
    }
    scene.entities = byId
  }

  const sceneName = opts.dxfFile.name.replace(/\.[^.]+$/, '.json')
  const sceneFile = new File(
    [JSON.stringify(scene)],
    sceneName,
    { type: 'application/json' }
  )

  const sceneAws = await uploadFile(sceneFile)
  if (!sceneAws) throw new Error('upload_scene_error')

  // 3. POST add_version — con scene_temp_key para que la PendingPictogramVersion
  // nazca con scene listo (permite "Continuar revisión" tras cerrar el browser).
  const verForm = new FormData()
  verForm.append('original_filename', opts.dxfFile.name)
  verForm.append('aws_response',      JSON.stringify(dxfAws))
  verForm.append('entity_type',       'pictogram')
  verForm.append('entity_id',         opts.pictogramId)
  verForm.append('scene_temp_key',    sceneAws.key)

  let verRes
  try {
    verRes = await addDocumentVersionForPictogram(
      opts.documentId,
      opts.pictogramId,
      verForm
    )
  } catch (err) {
    // axios lanza para !2xx. Detectamos el 422 "ya hay pendiente" para que el
    // caller pueda mostrar UI específica en vez de un toast genérico de error.
    const data = err?.response?.data
    if (err?.response?.status === 422 && data?.error === 'pending_version_exists') {
      const e = new Error(data.message || 'pending_version_exists')
      e.name = 'PendingVersionExists'
      e.pendingDocumentVersionId = data.pending?.document_version_id ?? null
      throw e
    }
    throw err
  }

  if (!verRes?.data?.success) {
    throw new Error(verRes?.data?.error || verRes?.data?.msg || 'add_version_error')
  }

  const documentVersionId = verRes.data.document_version_id
  if (!documentVersionId) {
    logger.error('add_version response sin id:', verRes.data)
    throw new Error('add_version_no_id')
  }

  return {
    documentVersionId: Number(documentVersionId),
    sceneKey: sceneAws.key,
  }
}
